import FileHandler from "@tiptap/extension-file-handler";
import type { Editor } from "@tiptap/react";
import {
  uploadImage,
  createBase64Preview,
  createErrorPlaceholder,
  generatePlaceholderId,
  findAndReplaceImagePlaceholder,
  setPlaceholderError,
} from "./uploadUtils";

export const getImageDimensions = (
  file: File,
): Promise<{ width: number; height: number }> => {
  return new Promise((resolve) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
      URL.revokeObjectURL(objectUrl); // Clean up
    };

    img.onerror = () => {
      resolve({ width: 0, height: 0 });
      URL.revokeObjectURL(objectUrl); // Clean up
    };

    img.src = objectUrl;
  });
};
const insertImagePlaceholder = async ({
  editor,
  pos,
  placeholderId,
  file,
}: {
  editor: Editor;
  pos: number;
  placeholderId: string;
  file: File;
}) => {
  const { width, height } = await getImageDimensions(file);
  const base64Preview = await createBase64Preview(file);

  // Calculate aspect ratio
  const aspectRatio = width && height ? width / height : null;
  // Constrain initial width to max 800px
  const displayWidth = Math.min(width || 300, 800);
  const displayHeight = aspectRatio
    ? displayWidth / aspectRatio
    : height || 200;

  editor
    .chain()
    .insertContentAt(pos, {
      type: "resizableImage",
      attrs: {
        src: base64Preview,
        alt: file.name,
        isLoading: true,
        width: displayWidth,
        height: displayHeight,
        alignment: "left",
        aspectRatio: aspectRatio,
        "data-placeholder-id": placeholderId,
      },
    })
    .focus()
    .run();
};

const insertFilePlaceholder = async ({
  editor,
  pos,
  placeholderId,
  file,
}: {
  editor: Editor;
  pos: number;
  placeholderId: string;
  file: File;
}) => {
  editor
    .chain()
    .insertContentAt(pos, {
      type: "fileChip",
      attrs: {
        type: file.type,
        src: "",
        name: file.name,
        isLoading: true,
        "data-placeholder-id": placeholderId,
      },
    })
    .focus()
    .run();
};

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const formatFileSize = (bytes: number): string =>
  `${(bytes / (1024 * 1024)).toFixed(1)}MB`;

export const handleImageUpload = async (
  editor: Editor,
  file: File,
  insertPos: number,
) => {
  const placeholderId = generatePlaceholderId();
  const isImage = file.type.startsWith("image/");

  // Insert loading placeholder
  if (isImage) {
    await insertImagePlaceholder({
      editor,
      pos: insertPos,
      placeholderId,
      file,
    });
  } else {
    await insertFilePlaceholder({
      editor,
      pos: insertPos,
      placeholderId,
      file,
    });
  }

  // Validate file size
  if (file.size > MAX_FILE_SIZE) {
    const errorMsg = `File exceeds 10MB limit (${formatFileSize(file.size)})`;
    setPlaceholderError(editor, placeholderId, errorMsg);
    return;
  }

  try {
    const result = await uploadImage(file);

    if (result.success && result.url) {
      // Replace placeholder with actual image
      findAndReplaceImagePlaceholder(editor, placeholderId, result.url);
    } else {
      // Replace with error placeholder
      findAndReplaceImagePlaceholder(
        editor,
        placeholderId,
        createErrorPlaceholder(),
      );
    }
  } catch (error) {
    console.error("Image upload error:", error);
    // Replace with error placeholder
    findAndReplaceImagePlaceholder(
      editor,
      placeholderId,
      createErrorPlaceholder(),
    );
  }
};

export const allowedMimeTypes = [
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
  "image/svg+xml",
  "text/csv",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/zip",
  "application/pdf",
];

export const ImageUploadExtension = FileHandler.configure({
  allowedMimeTypes: allowedMimeTypes,
  onDrop: (currentEditor, files, pos) => {
    files.forEach((file) => {
      handleImageUpload(currentEditor, file, pos);
    });
  },
  onPaste: (currentEditor, files) => {
    const insertPos = currentEditor.state.selection.anchor;
    files.forEach((file) => {
      handleImageUpload(currentEditor, file, insertPos);
    });
  },
});
